/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { Button, Dialog, InlineNotification, VisuallyHidden } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import BrowseProjectsDialogPlaceholder from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/BrowseProjectsDialogPlaceholder';
import BrowseProjectsProductCardsWithFilters from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/ProjectCardsWithFilters';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import useBrowseProjectsData from 'bundles/account-profile/components/recruiting-profile-viewer/queries/hooks/useBrowseProjectsData';
import type { Occupation } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/projectUtils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { SharedPortfolioProject_ProjectDetailsFragment as PortfolioProjectDetails } from 'bundles/portfolio-projects/queries/__generated__/SharedPortfolioProject_ProjectDetails';

import _t from 'i18n!nls/account-profile';

export type PropsFromCaller = {
  isOpen: boolean;
  onClose: () => void;
  occupations: Occupation[];
  dialogContentTrackingName?: typeof EVENTS.profileBrowseProjectsDialog;
  projectCardLinkTrackingName?: typeof EVENTS.profileBrowseProjectSelectProject;
  isLoading?: boolean;
  projects: PortfolioProjectDetails[];
};

type PropsToComponent = PropsFromCaller;

const { Actions, Content, HeadingGroup } = Dialog;

const styles = {
  // No good way to target the scroll container so used partial class attr
  // The gray bar is appearing because of sizing based on VW, VH and partial pixel
  // calculations.
  grayBarOffset: css`
    .cds-Dialog-dialog {
      > div[class*='ScrollContainer'] {
        margin-top: -1px;
        margin-left: -1px;
      }
    }
  `,
} as const;

export const BrowseProjectsDialog = ({
  isOpen,
  onClose,
  projects,
  isLoading,
  occupations,
  dialogContentTrackingName = EVENTS.profileBrowseProjectsDialog,
  projectCardLinkTrackingName = EVENTS.profileBrowseProjectSelectProject,
}: PropsToComponent) => {
  const { sharedEventingV3Data } = useContext(ProfileContext);

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_modal',
    {
      modal: {
        name: EVENTS.profileBrowseProjectsDialog,
      },
      ...sharedEventingV3Data,
    },
    // We need to add isOpen as a dependency because this component is rendered initially as closed and we need the visibility tracker to run again when it opens
    // so that the ref is mounted and the visibility tracker can track the modal view
    [isOpen]
  );

  if (isLoading) {
    return <BrowseProjectsDialogPlaceholder isOpen={isOpen} onClose={onClose} />;
  }

  return (
    <Dialog color="darkBlue" width="large" open={isOpen} onClose={onClose} css={styles.grayBarOffset}>
      <HeadingGroup
        title={_t('Browse Projects')}
        supportText={_t(
          'Projects give you real-world challenges to solve with industry tools, and produce work samples that help you demonstrate your capabilities and stand out.'
        )}
      >
        {_t('Browse Projects')}
      </HeadingGroup>

      <Content ref={visibilityTrackingRef}>
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          requireFullyVisible={false}
          trackingName={dialogContentTrackingName}
        >
          <VisuallyHidden role="status">{_t('Loaded filters and project cards')}</VisuallyHidden>

          <BrowseProjectsProductCardsWithFilters
            occupations={occupations}
            projects={projects}
            projectCardLinkTrackingName={projectCardLinkTrackingName}
          />
        </TrackedDiv>
      </Content>

      <Actions>
        <Button variant="primary" onClick={onClose}>
          {_t('Close')}
        </Button>
      </Actions>
    </Dialog>
  );
};

type PropsFromBrowseProjectsDialogWithDataCaller = {
  occupations: Occupation[];
  userId: string;
} & Omit<PropsFromCaller, 'projects'>;

const BrowseProjectsDialogWithData = ({
  onClose,
  isOpen,
  userId,
  occupations,
}: PropsFromBrowseProjectsDialogWithDataCaller) => {
  const { isLoading, projects } = useBrowseProjectsData(userId);

  return (
    <BrowseProjectsDialog
      occupations={occupations}
      isLoading={isLoading}
      projects={projects}
      onClose={onClose}
      isOpen={isOpen}
    />
  );
};

const ErrorBoundBrowseProjectsDialogWithData = (props: PropsFromBrowseProjectsDialogWithDataCaller) => (
  <ErrorBoundaryWithTagging
    componentName="BrowseProjectsDialog"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the browse projects dialog. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <BrowseProjectsDialogWithData {...props} />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundBrowseProjectsDialogWithData;
